import { useEffect, useRef, useState } from "react"
import { BiInfoCircle } from "react-icons/bi"
import { FiPlus } from "react-icons/fi"
import { IoIosInformationCircleOutline } from "react-icons/io"
import { IoAdd, IoChevronBack, IoClose } from "react-icons/io5"
import { RiDeleteBin6Line } from "react-icons/ri"
import { RxQuestionMarkCircled } from "react-icons/rx"

import { useAlert } from "@fuse/sharedComponents/globalAlert"
import { useGlobalLoader } from "@fuse/sharedComponents/globalLoader/GlobalLoaderContext"
import Switch from "@fuse/sharedComponents/Switch"
import {
  showErrorToast,
  showSuccessToast,
} from "@fuse/sharedComponents/themeToast/toastUtils"
import {
  EVENT_CLASS,
  EVENT_TICKET_TYPE,
  PAYMENT_TYPE,
} from "@letshang/constants"
import { settings } from "firebase/analytics"
import { filter, get, map } from "lodash-es"

import { createEventWeb } from "@api/firebaseWrappedApis"
import {
  useAddTicket,
  useDeleteTicket,
  useGetAllTickets,
  useUpdateTicket,
} from "@api/services/events/useEventsApi"

import TicketPriceInfoBottomSheet, {
  TicketPriceInfoBottomSheetHandle,
} from "../../createEvent-v5/TicketPriceInfoBottomSheet"

const inputBgClass = "bg-white/10"
const inputClass =
  "w-full rounded-xl text-white p-4 placeholder:text-[#A1A1B3] focus:outline-none border-none text-base font-normal"

const TicketForm = ({
  onCreate,
  isLoading,
}: {
  onCreate: (ticketData: any) => void
  isLoading: boolean
}) => {
  const [price, setPrice] = useState(0)
  const [priceInput, setPriceInput] = useState("")
  const [quantity, setQuantity] = useState(0)
  const [ticketName, setTicketName] = useState("")
  const [ticketDescription, setTicketDescription] = useState("")
  const [requireApproval, setRequireApproval] = useState(false)

  const handlePriceChange = (value: string) => {
    setPriceInput(value)

    const sanitizedValue = value.replace(/[^0-9.]/g, "").replace(/-/g, "")

    const parts = sanitizedValue.split(".")
    if (parts.length > 2) {
      return
    }

    if (parts[1]?.length > 2) {
      return
    }

    const numValue = Number(sanitizedValue)
    if (!isNaN(numValue) && numValue >= 0) {
      setPrice(numValue)
    }
  }

  const ticketPriceInfoBottomSheetRef =
    useRef<TicketPriceInfoBottomSheetHandle>(null)

  const handleOpenTicketPriceInfoBottomSheet = () => {
    ticketPriceInfoBottomSheetRef.current?.openModal()
  }

  const handleQuantityChange = (value: string) => {
    const sanitizedValue = value.replace(/[^0-9]/g, "").replace(/-/g, "")

    const numValue = Number(sanitizedValue)
    if (!isNaN(numValue) && numValue >= 0) {
      setQuantity(numValue)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const isQuantityInput =
      e.currentTarget.getAttribute("data-input-type") === "quantity"

    if (e.key === "-" || e.key === "Minus") {
      e.preventDefault()
    }

    // Prevent decimal point for quantity input
    if (isQuantityInput && (e.key === "." || e.key === "Decimal")) {
      e.preventDefault()
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    onCreate({
      ticketName,
      ticketDescriptions: ticketDescription,
      ticketPrice: price ?? 0,
      ticketQuantity: quantity,
      requireApproval,
    })
  }

  return (
    <>
      <form className="flex flex-col gap-6 w-full " onSubmit={handleSubmit}>
        <div>
          <label className="text-white block mb-2 text-left font-medium">
            Ticket name
          </label>
          <input
            className={`${inputClass} ${inputBgClass}`}
            placeholder="e.g. VIP ticket"
            maxLength={40}
            value={ticketName}
            onChange={(e) => setTicketName(e.target.value)}
            required
          />
          <div className="text-right text-xs text-[#A1A1B3]">
            {ticketName.length}/40
          </div>
        </div>
        <div>
          <label className="text-white block mb-2 text-left font-medium">
            Ticket description
          </label>
          <textarea
            className={`${inputClass} ${inputBgClass} min-h-[100px] resize-none`}
            placeholder="e.g. You'll receive access to the VIP lounge"
            maxLength={200}
            value={ticketDescription}
            onChange={(e) => setTicketDescription(e.target.value)}
            required
          />
          <div className="text-right text-xs text-[#A1A1B3]">
            {ticketDescription.length}/200
          </div>
        </div>
        <div>
          <label className="text-white block mb-2 text-left font-medium">
            <span className="flex items-center gap-2">
              Ticket price & availability
              <RxQuestionMarkCircled
                size={22}
                onClick={handleOpenTicketPriceInfoBottomSheet}
              />
            </span>
          </label>
          <div className="flex gap-4">
            <div className="flex-1 flex flex-col items-start gap-1">
              <div
                className={`flex items-center gap-2 ${inputBgClass} rounded-xl p-4 w-full`}
              >
                <span className="text-2xl">🎟️</span>
                <div className="flex flex-col w-full pl-3 border-l border-white/20">
                  <span className="text-[#A1A1B3] text-sm text-left">
                    Price
                  </span>
                  <div className="flex items-center gap-1">
                    {priceInput !== "" && (
                      <span className="text-white text-lg">$</span>
                    )}
                    <input
                      type="number"
                      min={0}
                      step={0.01}
                      className="bg-transparent text-white text-lg focus:outline-none w-full"
                      value={priceInput}
                      onChange={(e) => handlePriceChange(e.target.value)}
                      onKeyDown={handleKeyDown}
                      onWheel={(e) => e.currentTarget.blur()}
                      data-input-type="price"
                      placeholder="$1"
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="flex-1 flex flex-col items-start gap-1">
              <div
                className={`flex items-center gap-2 ${inputBgClass} rounded-xl p-4 w-full`}
              >
                <span className="text-2xl">🛒</span>
                <div className="flex flex-col w-full pl-3 border-l border-white/20">
                  <span className="text-[#A1A1B3] text-sm text-left">
                    Total tickets
                  </span>
                  <input
                    type="number"
                    min={1}
                    step={1}
                    className="bg-transparent text-white text-lg focus:outline-none w-full"
                    value={quantity === 0 ? "" : quantity}
                    onChange={(e) => handleQuantityChange(e.target.value)}
                    onWheel={(e) => e.currentTarget.blur()}
                    onKeyDown={handleKeyDown}
                    data-input-type="quantity"
                    placeholder="10"
                    required
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2">
          <Switch
            label="Require approval"
            checked={requireApproval}
            onChange={(e) => setRequireApproval(e.target.checked)}
            className="py-0"
          />
        </div>
        <button
          type="submit"
          disabled={isLoading}
          className={`w-full mt-4 bg-white/10 text-white rounded-full py-3 text-lg font-semibold transition ${
            isLoading ||
            !ticketName ||
            !ticketDescription ||
            price < 0 ||
            quantity <= 0
              ? "opacity-50 cursor-not-allowed"
              : ""
          }`}
        >
          {isLoading ? (
            <div className="flex items-center justify-center gap-2">
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            "Create ticket"
          )}
        </button>
      </form>
      <TicketPriceInfoBottomSheet
        ref={ticketPriceInfoBottomSheetRef}
        onClose={() => {
          ticketPriceInfoBottomSheetRef.current?.closeModal()
        }}
      />
    </>
  )
}

const TicketList = ({
  tickets,
  onDelete,
  onSelectTicket,
  onAddNew,
}: {
  tickets: any[]
  onDelete: (ticketId: string) => void
  onSelectTicket: (ticket: any) => void
  onAddNew: () => void
}) => {
  const { showAlert } = useAlert()

  const showDeleteAlert = (ticketId: string) => {
    showAlert(
      "Are you sure you want to delete this ticket?",
      "",
      "Delete tikcet",
      () => {
        onDelete(ticketId)
      },
      "Cancel",
      () => {},
    )
  }

  const TicketCard = ({ ticket, idx }) => {
    const totalTickets = ticket?.ticketQuantity
    const ticketsSold = get(ticket, "totalPurchased", 0)

    const isTicketSoldOut = ticketsSold >= totalTickets
    const isTicketDisabled = !ticket?.isActive

    return (
      <div
        key={ticket.id || idx}
        className={`rounded-xl p-4 flex flex-col gap-2 cursor-pointer ${inputBgClass}`}
        onClick={(e) => {
          e.stopPropagation()
          onSelectTicket(ticket)
        }}
      >
        <div className="flex justify-between items-center pb-2 border-b border-white/20">
          <div className="text-white text-left text-lg font-semibold">
            {ticket.ticketName}
          </div>
          <div className="flex items-center text-sm gap-2 text-white">
            <div
              className={`w-2 h-2 ${isTicketDisabled ? "bg-white" : isTicketSoldOut ? "bg-red-600" : "bg-green-500"} rounded-full`}
            />
            <span>
              {isTicketDisabled
                ? "Disabled"
                : isTicketSoldOut
                  ? "Sold out"
                  : "Available"}
            </span>
          </div>
        </div>
        <div className="flex flex-col mt-2 items-start">
          <div className="text-white">${ticket.ticketPrice}</div>
          <div className="text-[#A1A1B3]">
            {`${ticketsSold}/${totalTickets} tickets sold`}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-6 w-full h-full">
      <div className="flex flex-col gap-4 w-full flex-1 min-h-0">
        <div className="flex-1 min-h-0 flex flex-col gap-4 w-full overflow-y-auto no-scrollbar">
          {[...tickets]
            .sort((a, b) => a.ticketPrice - b.ticketPrice)
            .map((ticket, idx) => (
              <TicketCard ticket={ticket} idx={idx} />
            ))}
        </div>
        <button
          onClick={onAddNew}
          className={`rounded-xl p-4 flex flex-col gap-2 ${inputBgClass} border-2 border-dashed border-white/30 hover:border-white/50 transition-colors cursor-pointer`}
        >
          <div className="flex justify-center items-center py-2">
            <div className="flex items-center gap-3 text-white/70">
              <IoAdd size={24} />
              <span className="text-lg font-medium">Add another ticket</span>
            </div>
          </div>
        </button>
      </div>
      <div className="flex gap-2 text-white/50 text-md flex-shrink-0">
        <span className="text-xl">ℹ️</span>
        <span className="text-left text-md">
          Payments are powered by Stripe. Host is responsible for managing
          chargebacks.{" "}
          <a
            href="https://docs.stripe.com/disputes"
            className="!text-white/50 underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            Learn more
          </a>
        </span>
      </div>
    </div>
  )
}

const TicketDetails = ({ selectedTicket, canDisable, refetchTickets }) => {
  const { showLoader, hideLoader } = useGlobalLoader()
  const { mutate: updateTicket } = useUpdateTicket()

  const [isDisabled, setIsDisabled] = useState(!selectedTicket?.isActive)
  const [wasToggledManually, setWasToggledManually] = useState(false)

  const handleSwitchChange = (value: boolean) => {
    if (!canDisable && value) {
      showErrorToast("At least one active ticket is required", {
        text2: "Add another ticket before disabling this one.",
      })
      return
    }
    setIsDisabled(value)
    setWasToggledManually(true) // flag manual change
  }

  const onUpdateTicket = async () => {
    const payload = {
      eventId: selectedTicket?.eventId,
      ticketId: selectedTicket?.id,
      newTicketData: {
        ...selectedTicket,
        isActive: !isDisabled,
      },
    }

    showLoader()
    updateTicket(payload, {
      onSuccess: async (res) => {
        if (get(res, "success", false)) {
          refetchTickets()
          showSuccessToast(
            `Ticket successfully ${isDisabled ? "disabled" : "enabled"}`,
          )
        } else {
          showErrorToast(res?.message ?? "Something went wrong")
        }
        hideLoader()
      },
      onError: (res) => {
        showErrorToast(
          get(res, "message", "Something went wrong") ?? "Something went wrong",
        )
        hideLoader()
      },
    })
  }

  useEffect(() => {
    if (wasToggledManually) {
      // This will only run on manual toggle
      console.log("Switch manually changed to:", isDisabled)

      onUpdateTicket()
      // Optional: perform actions here (e.g., API call)

      setWasToggledManually(false) // reset the manual flag
    }
  }, [isDisabled, wasToggledManually])

  return (
    <div className="space-y-6 text-white flex-col text-start">
      {/* Ticket Name */}
      <div>
        <p className="text-sm text-gray-400">Ticket name</p>
        <p className="text-lg font-medium">{selectedTicket?.ticketName}</p>
      </div>

      {/* Ticket Description */}
      <div>
        <p className="text-sm text-gray-400">Ticket description</p>
        <p className="text-base text-gray-300">
          {selectedTicket?.ticketDescriptions}
        </p>
      </div>

      {/* Ticket Price */}
      <div>
        <p className="text-sm text-gray-400">Ticket price</p>
        <p className="text-lg font-medium">
          {`$${selectedTicket?.ticketPrice}`}
        </p>
      </div>

      {/* Quantity */}
      <div>
        <p className="text-sm text-gray-400">Quantity</p>
        <p className="text-lg font-medium">{selectedTicket?.ticketQuantity}</p>
      </div>

      {/* Approval Settings */}
      <div>
        <p className="text-sm text-gray-400">Approval settings</p>
        <p className="text-base text-gray-300">
          {`${selectedTicket?.requireApproval ? "Requires Approval" : "No approval required"}`}
        </p>
      </div>

      {/* Toggle Switch */}
      <div className="flex justify-between items-center pt-4">
        <span className="text-lg font-medium">Disable</span>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={isDisabled}
            onChange={() => handleSwitchChange(!isDisabled)}
          />
          <div className="w-11 h-6 bg-gray-600 rounded-full peer peer-checked:bg-white peer-checked:bg-opacity-20 after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:after:translate-x-full peer-checked:after:bg-white"></div>
        </label>
      </div>
    </div>
  )
}

const EditTicket = ({
  isOpen,
  onClose,
  eventId,
  localTickets,
}: {
  isOpen: boolean
  onClose: () => void
  eventId: string
  localTickets?: any[]
}) => {
  const [showForm, setShowForm] = useState(false)
  const [showEditTicket, setShowEditTicket] = useState(false)
  const [selectedTicket, setSelectedTicket] = useState({})

  const [isCreatingTicket, setIsCreatingTicket] = useState(false)
  const [isDeletingTicket, setIsDeletingTicket] = useState(false)

  const { mutate } = useAddTicket()
  const { mutate: deleteTicket } = useDeleteTicket()
  const {
    data: ticketsData,
    refetch: refetchTickets,
    isLoading,
  } = useGetAllTickets(eventId)
  const apiTickets = get(ticketsData, "data.tickets", [])

  const tickets = !isLoading ? apiTickets : localTickets

  const canDisable = filter(map(tickets, "isActive"), Boolean).length > 1

  useEffect(() => {
    if (eventId) {
      refetchTickets()
    }
  }, [eventId])

  // Show form directly if no tickets exist
  useEffect(() => {
    if (tickets.length > 0) {
      setShowForm(false)
    } else {
      setShowForm(true)
    }
  }, [tickets])

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden"
    } else {
      document.body.style.overflow = ""
    }
    return () => {
      document.body.style.overflow = ""
    }
  }, [isOpen])

  const handleModalClose = () => {
    // Reset form state when closing
    setShowForm(false)
    setShowEditTicket(false)
    onClose()
  }

  const handleCreate = (ticketData: any) => {
    setIsCreatingTicket(true)
    mutate(
      {
        eventId,
        ticketData: {
          ticketType:
            ticketData.ticketPrice === 0
              ? EVENT_TICKET_TYPE.FREE_TICKET
              : EVENT_TICKET_TYPE.PAID_TICKET,
          paymentType: PAYMENT_TYPE.MANUAL,
          eventId: eventId,
          ...ticketData,
          isActive: true,
        },
      },
      {
        onSuccess: async (response) => {
          if (response?.success) {
            showSuccessToast("Ticket created successfully")
            refetchTickets()
            setShowForm(false)
          } else {
            showErrorToast("Something went wrong.")
          }
          setIsCreatingTicket(false)
        },
        onError: () => {
          showErrorToast("Something went wrong.")
          setIsCreatingTicket(false)
        },
      },
    )
  }

  const handleDelete = (ticketId: string) => {
    setIsDeletingTicket(true)
    deleteTicket(
      {
        eventId,
        ticketId,
      },
      {
        onSuccess: async () => {
          refetchTickets()
          setIsDeletingTicket(false)
        },
        onError: () => {
          showErrorToast("Something went wrong.")
          setIsDeletingTicket(false)
        },
      },
    )
  }

  const handleAddNew = () => setShowForm(true)

  const onSelectTicket = (ticket) => {
    setSelectedTicket(ticket)
    setShowEditTicket(true)
  }

  const onHideEditTicket = () => {
    setShowEditTicket(false)
    setSelectedTicket({})
  }

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/20"
      onClick={handleModalClose}
      style={{ pointerEvents: "auto" }}
    >
      <div
        className="w-full md:max-w-md  backdrop-blur-xl rounded-t-[32px] rounded-b-none p-5 pt-6 flex flex-col items-center shadow-2xl animate-slideUp relative h-full max-h-[80%] overflow-y-auto no-scrollbar"
        style={{
          borderTopLeftRadius: 32,
          borderTopRightRadius: 32,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* <div className="w-14 h-1 rounded-full bg-white/20 opacity-60 mb-4 -mt-2" /> */}
        <div className="flex justify-between items-center w-full mb-6 mt-3">
          {showEditTicket ? (
            <button
              className="w-[33%] text-white text-3xl p-0 bg-transparent transition flex justify-start items-center"
              onClick={onHideEditTicket}
              aria-label="Close"
              type="button"
            >
              <span className="bg-white/20 rounded-full p-2">
                <IoChevronBack className="text-xl" />
              </span>
            </button>
          ) : (
            <div className="flex-1" />
          )}

          <h2 className="flex-1 text-white text-2xl font-medium text-center">
            {showForm ? "New ticket" : "Tickets"}
          </h2>

          {showEditTicket ? (
            <div className="flex-1" />
          ) : (
            <button
              className="w-[33%] text-white text-3xl p-0 bg-transparent transition flex justify-end items-center"
              onClick={handleModalClose}
              aria-label="Close"
              type="button"
            >
              <IoClose />
            </button>
          )}
        </div>
        <div className="w-full max-w-[400px] mx-auto h-full flex flex-col min-h-0">
          {showForm ? (
            <TicketForm onCreate={handleCreate} isLoading={isCreatingTicket} />
          ) : showEditTicket ? (
            <TicketDetails
              selectedTicket={selectedTicket}
              canDisable={canDisable}
              refetchTickets={refetchTickets}
            />
          ) : (
            <TicketList
              tickets={tickets}
              onDelete={handleDelete}
              onSelectTicket={onSelectTicket}
              onAddNew={handleAddNew}
            />
          )}
        </div>
      </div>
      <style>{`
        .animate-slideUp {
          animation: slideUpSheet 0.35s cubic-bezier(0.4, 0, 0.2, 1);
        }
        @keyframes slideUpSheet {
          from {
            transform: translateY(100%);
            opacity: 0.7;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </div>
  )
}

export default EditTicket
